import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { gql } from 'graphql-request'

import { Product } from '@/app/book/api/useProductLineApi'
import { useGraphQLClient } from '@/hooks/useGraphQLClient'
import { ShipRoute } from '@/types/order'

const FIVE_MINUTES = 7 * 24 * 60 * 60 * 1000

const GET_CARRIER_SERVICE_LEVELS_QUERY = gql`
  query GetCarrierServiceLevels($input: CarrierServiceOptionsInput!) {
    carrierServices: getCarrierServices(input: $input) {
      id
      displayName
      systemName
      shipDate
      arrivalDate
      carrierServiceLevelId
      serviceOptions {
        productId
        priceCents
        adjustedPriceCents
        packageCount
        serviceRateId
      }
    }
  }
`

interface CarrierServiceLevelsResponse {
  carrierServices: CarrierService[]
}

export interface CarrierService {
  id: string
  displayName: string
  systemName: string
  combinedAdjustedPriceCents?: number
  shipDate: string // MM/DD/YYYY
  arrivalDate: string // MM/DD/YYYY
  carrierServiceLevelId: string
  serviceOptions: ServiceOption[]
  adminLabel?: 'preferred' | 'offline'
  totalComputedPriceNote?: number
}

export interface ServiceOption {
  productId: string
  priceCents: number
  adjustedPriceCents: number
  recommended?: boolean
  time_note?: string
}

export const getServiceOptionPrice = (serviceOptions: ServiceOption[], productId: string): number =>
  serviceOptions?.find((option) => option.productId === productId)?.priceCents || 0

export const computePricesForCarrierServices = (
  carrierServices: CarrierService[],
  products: Product[],
): CarrierService[] =>
  carrierServices?.map((serviceLevel) => {
    const totalComputedPriceNote = products.reduce(
      (sum: number, product) =>
        sum + getServiceOptionPrice(serviceLevel?.serviceOptions, product.id),
      0,
    )
    return { ...serviceLevel, totalComputedPriceNote }
  })

/**
 * Hook to fetch product lines based on the ship route
 * @param shipRoute - The ship route to fetch product lines for
 * @returns Query result containing product lines data
 */
export const useCarrierServiceApi = (
  shipRoute: ShipRoute,
  productIds: string[],
  shipDate: string, // YYYY-MM-DD
  dropOff: boolean,
  direction: 'inbound' | 'outbound',
  //  carrier?: string,
): UseQueryResult<CarrierService[], Error> => {
  const client = useGraphQLClient()
  const { origin, destination } = shipRoute

  // TODO: Add tests for GraphQL query failures and invalid response data
  const fetchCarrierServiceLevels = async (): Promise<CarrierService[]> => {
    const { carrierServices } = await client.request<CarrierServiceLevelsResponse>(
      GET_CARRIER_SERVICE_LEVELS_QUERY,
      {
        input: {
          shipRoute,
          productIds,
          shipDate,
          dropOff,
          direction,
          // TODO: add `carrier` during Admin development
        },
      },
    )
    return carrierServices
  }

  return useQuery<CarrierService[], Error>({
    queryKey: [
      'carrierServiceLevels',
      shipDate,
      JSON.stringify(productIds),
      dropOff,
      // TODO: check against all props of the shipRoute
      JSON.stringify(origin),
      JSON.stringify(destination),
    ],
    queryFn: fetchCarrierServiceLevels,
    staleTime: FIVE_MINUTES,
    gcTime: FIVE_MINUTES,
    enabled:
      productIds.length > 0 &&
      !!shipDate &&
      !!Object.keys(origin).length &&
      !!Object.keys(destination).length,
    // TODO: add !!carrier during Admin development
  })
}
